import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Coins, Sparkles } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [goals, setGoals] = useState([]);
  const [roundup, setRoundup] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/goals").then(r => setGoals(r.data));
    api.get("/roundup/summary").then(r => setRoundup(r.data));
    api.get("/subscription/status").then(r => setSub(r.data)).catch(() => {});
  }, []);

  const upgrade = async (tier) => {
    const coupon = window.prompt("Have a coupon code? Enter it (or leave blank).");
    try {
      const r = await api.post("/payments/checkout", { tier, origin_url: window.location.origin, coupon_code: coupon || undefined });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error("Checkout failed: " + (e?.response?.data?.detail || e.message)); }
  };

  const cancel = async () => {
    if (!window.confirm("Before you cancel — would you like to downgrade to Lite ($4.99/mo) instead? Click OK to see Lite, Cancel to fully cancel.")) {
      const reason = window.prompt("Optional: tell us why you're canceling (helps us improve)");
      if (reason === null) return;
      try {
        const r = await api.post("/subscription/cancel", { reason });
        toast.success("Subscription will end at period close", { description: r.data.cancel_at_period_end ? "Access continues until then" : "" });
        const s = await api.get("/subscription/status"); setSub(s.data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
      return;
    }
    try {
      const r = await api.post("/subscription/downgrade", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Downgrade failed"); }
  };

  const saveRoundup = async (enabled, goal_id) => {
    await api.post("/roundup/settings", { enabled, goal_id });
    const r = await api.get("/roundup/summary"); setRoundup(r.data);
    toast.success(enabled ? "Round-ups on" : "Round-ups paused");
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h1 className="font-heading text-3xl font-bold mb-8">Settings</h1>

      <section className="glass-card p-6 mb-6">
        <h2 className="font-heading text-lg font-semibold mb-4">Profile</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Name</span><span>{user?.name}</span></div>
          <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Email</span><span>{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Plan</span><span className="capitalize font-medium text-[color:var(--gold)]">{user?.tier || "free"}</span></div>
          {sub?.subscription_status === "trialing" && sub?.trial_end && (
            <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Trial ends</span><span className="text-[color:var(--gold)]">{new Date(sub.trial_end).toLocaleString()}</span></div>
          )}
          {sub?.subscription_status === "canceling" && sub?.cancel_at && (
            <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Ends on</span><span className="text-[color:var(--terracotta)]">{new Date(sub.cancel_at).toLocaleDateString()}</span></div>
          )}
        </div>
        {sub?.tier !== "free" && sub?.subscription_status !== "canceling" && (
          <button data-testid="cancel-sub-btn" onClick={cancel} className="mt-4 text-xs text-[color:var(--terracotta)] hover:underline">Cancel subscription</button>
        )}
      </section>

      {/* Round-up Savings */}
      {roundup && (
        <section data-testid="roundup-section" className="glass-card p-6 mb-6 border-[rgba(212,175,55,0.25)] relative overflow-hidden">
          <div className="absolute inset-0 shimmer pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Coins className="w-5 h-5 text-[color:var(--gold)]" />
              <h2 className="font-heading text-lg font-semibold">Round-Up Savings</h2>
            </div>
            <p className="text-sm text-[color:var(--text-secondary)] mb-4">Every purchase rounds up to the nearest dollar and auto-transfers the change into a goal you choose.</p>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">This month</div><div data-testid="roundup-this-month" className="font-mono text-xl font-bold gold-gradient-text">${roundup.this_month}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Purchases counted</div><div className="font-mono text-xl font-bold">{roundup.count}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Projected / year</div><div className="font-mono text-xl font-bold text-[color:var(--emerald)]">${roundup.projected_year}</div></div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <select data-testid="roundup-goal" value={roundup.goal?.goal_id || ""} onChange={(e) => saveRoundup(true, e.target.value || null)}
                className="flex-1 px-4 py-3 rounded-xl bg-[color:var(--bg-elevated)] border border-[rgba(212,175,55,0.15)] outline-none text-sm">
                <option value="">Choose a goal...</option>
                {goals.map(g => <option key={g.goal_id} value={g.goal_id}>{g.title}</option>)}
              </select>
              <button data-testid="roundup-toggle" onClick={() => saveRoundup(!roundup.enabled, roundup.goal?.goal_id)}
                className={`px-5 py-3 rounded-full text-sm font-semibold transition inline-flex items-center gap-2 ${roundup.enabled ? "gold-btn" : "border border-[rgba(212,175,55,0.3)] hover:border-[color:var(--gold)]"}`}>
                {roundup.enabled ? <><Check className="w-4 h-4" /> On</> : "Turn on"}
              </button>
            </div>
            {roundup.enabled && roundup.goal && <div className="text-xs text-[color:var(--emerald)] mt-3 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Rounding up into <span className="font-medium">{roundup.goal.title}</span></div>}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-heading text-xl font-bold mb-4">Subscription</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Premium</div>
            <div className="mb-1"><span className="font-mono text-4xl font-bold">$12.99</span><span className="text-[color:var(--text-secondary)]">/mo</span></div>
            <div className="text-xs text-[color:var(--emerald)] mb-5">7 days free · then $12.99/mo · cancel anytime</div>
            <ul className="space-y-2 mb-6 text-sm">
              {["Unlimited AI coaching", "Personalized plan", "Weekly check-ins", "Goals & debt optimization"].map(x => (
                <li key={x} className="flex items-start gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)] mt-0.5" /> {x}</li>
              ))}
            </ul>
            <button data-testid="upgrade-premium-btn" onClick={() => upgrade("premium")}
              className="w-full py-3 rounded-full border border-[rgba(212,175,55,0.3)] hover:border-[color:var(--gold)] transition">Start 7-day free trial</button>
          </div>
          <div className="glass-card p-6 border-[rgba(212,175,55,0.4)]">
            <div className="text-xs uppercase tracking-widest text-[color:var(--gold)] mb-2">Premium+</div>
            <div className="mb-1"><span className="font-mono text-4xl font-bold gold-gradient-text">$29.99</span><span className="text-[color:var(--text-secondary)]">/mo</span></div>
            <div className="text-xs text-[color:var(--emerald)] mb-5">7 days free · then $29.99/mo · cancel anytime</div>
            <ul className="space-y-2 mb-6 text-sm">
              {["Everything in Premium", "Human coach access", "Family / partner shared view", "Advanced planning"].map(x => (
                <li key={x} className="flex items-start gap-2"><Check className="w-4 h-4 text-[color:var(--emerald)] mt-0.5" /> {x}</li>
              ))}
            </ul>
            <button data-testid="upgrade-premium-plus-btn" onClick={() => upgrade("premium_plus")}
              className="gold-btn w-full py-3 rounded-full">Start 7-day free trial</button>
          </div>
        </div>
      </section>
    </div>
  );
}
